import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import '../../../assets/css/home/CategoriesSection.css';

const CategoriesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [visibleCards, setVisibleCards] = useState([]);

  const categories = [
    {
      id: 1,
      // title: 'Food Donation',
      icon: '🍽',
      description: 'Share meals and bring hope to hungry families. Every donation feeds someone in need.',
      image: 'https://images.unsplash.com/photo-1593113616828-c43c2b10fe16?w=800&q=80',
      color: '#165C5C',
      gradient: 'linear-gradient(135deg, rgba(22, 92, 92, 0.95), rgba(34, 139, 139, 0.85))',
      stats: {
        icon: '📊',
        number: '50K+',
        label: 'Meals Served'
      },
      features: [
        { icon: '✓', text: 'Fresh Cooked Meals' },
        { icon: '✓', text: 'Monthly Rations' },
        { icon: '✓', text: 'Emergency Food Aid' }
      ]
    },
    {
      id: 2,
      // title: 'Medicine Donation',
      icon: '💊',
      description: 'Provide life-saving medicines and help patients access proper healthcare treatments.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
      color: '#FFB800',
      gradient: 'linear-gradient(135deg, rgba(255, 184, 0, 0.95), rgba(255, 153, 0, 0.85))',
      stats: {
        icon: '🏥',
        number: '30K+',
        label: 'Patients Helped'
      },
      features: [
        { icon: '✓', text: 'Free Medicines' },
        { icon: '✓', text: 'Medical Equipment' },
        { icon: '✓', text: 'Treatment Support' }
      ]
    },
    {
      id: 3,
      // title: 'Blood Donation',
      icon: '🩸',
      description: 'Donate blood and save precious lives. One donation can save up to three people.',
      image: 'https://images.unsplash.com/photo-1615461065929-4f8ffed6ca40?w=800&q=80',
      color: '#E90C36',
      gradient: 'linear-gradient(135deg, rgba(233, 12, 54, 0.95), rgba(220, 38, 38, 0.85))',
      stats: {
        icon: '❤',
        number: '15K+',
        label: 'Lives Saved'
      },
      features: [
        { icon: '✓', text: 'Emergency Blood' },
        { icon: '✓', text: 'Blood Drives' },
        { icon: '✓', text: 'Donor Registry' }
      ]
    }
  ];

  // Progressive card reveal animation - FIXED
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const initialDelay = setTimeout(() => {
      categories.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 400); // 400ms stagger between cards
      });
    }, 100);

    return () => clearTimeout(initialDelay);
  }, []);

  return (
    <section className="cat-categories-section">
      <Container>
        {/* Section Header - Animated */}
        <div className="cat-section-header">
          <div className="cat-header-badge">
            <span className="cat-badge-icon">⭐</span>
            <span className="cat-badge-text">Our Categories</span>
          </div>
          <h2 className="cat-section-title">
            Choose Your Way To
            <span className="cat-title-highlight"> Make Impact</span>
          </h2>
          <p className="cat-section-description">
            Every contribution counts. Select a category and start changing lives today.
          </p>
          <div className="cat-title-underline">
            <span className="cat-underline-dot"></span>
            <span className="cat-underline-line"></span>
            <span className="cat-underline-dot"></span>
          </div>
        </div>

        {/* Categories Grid - One Line */}
        <Row>
          {categories.map((category, index) => (
            <Col lg={4} md={4} sm={12} className="mb-4" key={category.id}>
              <Card 
                className={`cat-category-card ${visibleCards.includes(index) ? 'cat-card-visible' : 'cat-card-hidden'} ${hoveredCard === category.id ? 'cat-card-hovered' : ''}`}
                onMouseEnter={() => setHoveredCard(category.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Animated Border */}
                <div className="cat-card-border" style={{ background: category.color }}></div>

                {/* Image Background with Particles */}
                <div className="cat-card-image-wrapper">
                  <div 
                    className="cat-card-bg-image"
                    style={{ backgroundImage: `url(${category.image})`}}
                  >
                    <div 
                      className="cat-image-overlay"
                      style={{ background: category.gradient }}
                    ></div>
                  </div>
                  
                  {/* Animated Particles */}
                  <div className="cat-particles">
                    <span className="cat-particle cat-particle-1" style={{ color: category.color }}>✦</span>
                    <span className="cat-particle cat-particle-2" style={{ color: category.color }}>✦</span>
                    <span className="cat-particle cat-particle-3" style={{ color: category.color }}>✦</span>
                  </div>

                  {/* Floating Icon with Circle */}
                  <div className="cat-floating-icon-wrapper">
                    <div className="cat-icon-circle" style={{ 
                      background: 'radial-gradient(circle, rgba(255,255,255,0.9), rgba(255,255,255,0.7))',
                      boxShadow: `0 20px 60px ${category.color}40`
                    }}>
                      <span className="cat-category-large-icon">{category.icon}</span>
                    </div>
                    <div className="cat-icon-ring" style={{ borderColor: category.color }}></div>
                    <div className="cat-icon-ring-2" style={{ borderColor: category.color }}></div>
                  </div>

                  {/* Stats Badge - Modern Design */}
                  <div className="cat-stats-badge" style={{ 
                    background: `linear-gradient(135deg, ${category.color}, ${category.color}dd)` 
                  }}>
                    <div className="cat-stats-icon-wrapper">
                      <span className="cat-stats-icon">{category.stats.icon}</span>
                    </div>
                    <div className="cat-stats-content">
                      <strong>{category.stats.number}</strong>
                      <span>{category.stats.label}</span>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <Card.Body className="cat-card-content">
                  {/* Title with animated underline */}
                  <div className="cat-title-wrapper">
                    <h3 className="cat-category-title" style={{ color: category.color }}>
                      {category.title}
                    </h3>
                    <div className="cat-title-bar" style={{ backgroundColor: category.color }}></div>
                  </div>
                  
                  <p className="cat-category-description">
                    {category.description}
                  </p>

                  {/* Features List - Animated */}
                  <ul className="cat-features-list">
                    {category.features.map((feature, idx) => (
                      <li key={idx} className="cat-feature-item">
                        <div className="cat-feature-icon-wrapper" style={{ backgroundColor: `${category.color}15` }}>
                          <span className="cat-feature-icon" style={{ color: category.color }}>
                            {feature.icon}
                          </span>
                        </div>
                        <span className="cat-feature-text">{feature.text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button - Premium Style */}
                  <Button 
                    className="cat-category-btn"
                    style={{ 
                      background: `linear-gradient(135deg, ${category.color}, ${category.color}dd)`,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="cat-btn-content">
                      <span className="cat-btn-text">Explore Category</span>
                      <span className="cat-btn-icon-wrapper">
                        <span className="cat-btn-arrow">→</span>
                      </span>
                    </span>
                  </Button>
                </Card.Body>

                {/* Glow Effect on Hover */}
                <div className="cat-card-glow" style={{ 
                  background: `radial-gradient(circle at center, ${category.color}40, transparent)` 
                }}></div>

                {/* Wave Animation */}
                <div className="cat-wave-animation">
                  <svg viewBox="0 0 500 150" preserveAspectRatio="none">
                    <path d="M0,100 C150,200 350,0 500,100 L500,150 L0,150 Z" style={{ fill: `${category.color}15` }}></path>
                  </svg>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Bottom Info Bar - Animated */}
        <div className="cat-info-bar">
          <div className="cat-info-item">
            <div className="cat-info-icon">🎯</div>
            <div className="cat-info-content">
              <strong>100% Verified</strong>
              <span>All donations tracked</span>
            </div>
          </div>
          <div className="cat-info-divider"></div>
          <div className="cat-info-item">
            <div className="cat-info-icon">⚡</div>
            <div className="cat-info-content">
              <strong>Instant Impact</strong>
              <span>Real-time updates</span>
            </div>
          </div>
          <div className="cat-info-divider"></div>
          <div className="cat-info-item">
            <div className="cat-info-icon">🔒</div>
            <div className="cat-info-content">
              <strong>Secure & Safe</strong>
              <span>Protected donations</span>
            </div>
          </div>
        </div>
      </Container>

      {/* Animated Background Elements */}
      <div className="cat-bg-elements">
        <div className="cat-bg-circle cat-circle-1"></div>
        <div className="cat-bg-circle cat-circle-2"></div>
        <div className="cat-bg-circle cat-circle-3"></div>
      </div>
    </section>
  );
};

export default CategoriesSection;